import VMind, {Model} from '@visactor/vmind';
import {ChartConfig, GridPluginProps} from '../types';

// 定义进度回调的类型
export type ChartGenerationProgress = {
  index: number;
  status: 'generating' | 'success' | 'error';
  chartConfig?: ChartConfig | null;
  error?: string;
};
export type ChartGenerationCallback = (
  progress: ChartGenerationProgress
) => void;
export interface AIChartGenerationOptions {
  sheetId: string;
  data: any[];
  fields: any[];
  maxCharts?: number;
  customPrompt?: string; // 新增自定义提示词参数
}

export class AIChartService{
  private vmind: VMind;

  constructor(props: GridPluginProps) {
    // 构建基础配置
    const vmindConfig: any = {
      url: props?.aiUrl || 'http://192.168.2.58:30000/v1/chat/completions',
      model: props?.aiModel || 'qwen/qwen3-30b-a3b-2507'
    };
    // url: 'https://api.openai.com/v1/chat/completions',
    // model: Model.GPT4,
    // headers: {
    //   'Authorization': 'Bearer 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'
    // }
    // url: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
    // model: 'qwen-max',
    // headers: {
    //   Authorization: 'Bearer sk-30067955450f4cb58e320098f0409b0a'
    // }
    // url: 'http://192.168.2.58:30000/v1/chat/completions',
    // model: 'qwen/qwen3-30b-a3b-2507',
    // headers: {
    //   Authorization: 'Bearer sk-30067955450f4cb58e320098f0409b0a'
    // }
    // 只有当 apiKey 存在时才添加 headers
    if (props?.apiKey && props.apiKey.trim()) {
      vmindConfig.headers = {
        'Authorization': `Bearer ${props.apiKey}`
      };
    }
    // 初始化VMind实例
    this.vmind = new VMind(vmindConfig);
  }

  /**
   * 智能生成多个图表配置（流式，带进度回调）
   */
  async generateChartsWithProgress(
    options: AIChartGenerationOptions,
    onProgress: ChartGenerationCallback
  ): Promise<void> {
    const {data, fields, maxCharts = 4, customPrompt, sheetId} = options;

    if (!data || data.length === 0) {
      throw new Error('数据为空，无法生成图表');
    }

    try {
      const fieldInfo = this.vmind.getFieldInfo(data);

      let prompts: string[];
      let chartTitles: string[];

      if (customPrompt && customPrompt.trim()) {
        prompts = this.generateCustomPrompts(
          fieldInfo,
          customPrompt
        );
        chartTitles = prompts.map((prompt, index) =>
          this.extractTitleFromPrompt(prompt, customPrompt)
        );
      } else {
        prompts = this.generateDefaultPrompts(fieldInfo, maxCharts);
        chartTitles = prompts.map((prompt, index) =>
          this.extractTitleFromPrompt(prompt)
        );
      }
      // 逐一生成图表并实时报告进度
      for (let i = 0; i < prompts.length; i++) {
        // 报告开始生成
        onProgress({index: i, status: 'generating'});

        try {
          const res = await this.vmind.generateChart(
            prompts[i],
            fieldInfo,
            data,
            {enableDataQuery: true}
          );
          console.log("大模型返回", res);
          
          const {spec} = res;

          if (spec) {
            console.log(`生成第${i + 1}个图表成功:`, spec);
            
            const chartConfig: ChartConfig = {
              id: i,
              code: `ai-chart-preview-${Date.now()}-${i}`,
              title: chartTitles[i],
              dataSource: sheetId || '',
              chartType: this.extractChartTypeFromSpec(spec),
              // chartSpec: {...spec, data: {values: data}},
              chartSpec: spec,

              layout: {i: `layout-${i}`, x: 0, y: 0, w: 6, h: 8} // 临时布局
            };
            // 报告成功
            onProgress({index: i, status: 'success', chartConfig});
          } else {
            throw new Error('AI未返回有效的图表配置');
          }
        } catch (error: any) {
          console.error(`生成第${i + 1}个图表失败:`, error);
          // 报告失败
          onProgress({
            index: i,
            status: 'error',
            error: error.message || '生成失败'
          });
        }
      }
    } catch (error) {
      console.error('AI生成图表失败:', error);
      // 抛出异常，让调用方处理
      throw error;
    }
  }

  /**
   * 基于自定义提示词生成图表提示 - 优化版本
   */
  private generateCustomPrompts(
    fieldInfo: any,
    customPrompt: string
  ): string[] {
    const prompts: string[] = [];

    // 分析字段类型，为自定义提示词提供上下文
    const categoricalFields = fieldInfo.filter(
      (field: any) => field.type === 'string'
    );
    const numericalFields = fieldInfo.filter(
      (field: any) => field.type === 'number'
    );
    const dateFields = fieldInfo.filter((field: any) => field.type === 'date');

    // 构建字段信息上下文
    const fieldContext = this.buildFieldContext(
      categoricalFields,
      numericalFields,
      dateFields
    );
    // const basePrompt = `${customPrompt}. The generated x-axis and y-axis fields must be fields from the dataset`;
    const basePrompt = `${customPrompt}`;
    // 生成基于用户需求的多样化图表
    prompts.push( basePrompt );

    return prompts;
  }

  /**
   * 生成默认的图表提示（进一步优化版本）
   */
  private generateDefaultPrompts(fieldInfo: any, maxCharts: number): string[] {
    const prompts: string[] = [];
    const generatedChartKeys = new Set<string>(); // 用于避免重复图表

    // 分析字段类型
    const categoricalFields = fieldInfo.filter(
      (field: any) => field.type === 'string'
    );
    const numericalFields = fieldInfo.filter(
      (field: any) => field.type === 'number'
    );
    const dateFields = fieldInfo.filter((field: any) => field.type === 'date');

    console.log('categoricalFields:', categoricalFields);
    console.log('numericalFields:', numericalFields);
    console.log('dateFields:', dateFields);

    // 1. 为每个分类字段生成分布统计柱状图
    for (const catField of categoricalFields) {
      if (prompts.length >= maxCharts) break;

      const chartKey = `distribution-${catField.fieldName}`;
      if (generatedChartKeys.has(chartKey)) continue;

      prompts.push(
        `请根据数据集中的"${catField.fieldName}"字段创建一个柱状图，展示该字段各分类的数量分布情况。使用柱状图类型，以"${catField.fieldName}"作为X轴分类，Y轴显示计数。请确保图表清晰展示每个分类的数量差异。`
      );
      generatedChartKeys.add(chartKey);
    }

    // 2. 为每个分类字段生成饼图
    for (const catField of categoricalFields) {
      if (prompts.length >= maxCharts) break;

      const chartKey = `pie-${catField.fieldName}`;
      if (generatedChartKeys.has(chartKey)) continue;

      prompts.push(
        `请根据数据集中的"${catField.fieldName}"字段创建一个饼图，展示该字段各分类的占比分布。使用饼图类型，显示各分类的百分比比例。请确保饼图能清晰反映各分类的占比关系。`
      );
      generatedChartKeys.add(chartKey);
    }

    // 3. 数值字段间的相关性分析
    for (
      let i = 0;
      i < numericalFields.length && prompts.length < maxCharts;
      i++
    ) {
      for (
        let j = i + 1;
        j < numericalFields.length && prompts.length < maxCharts;
        j++
      ) {
        const xField = numericalFields[i];
        const yField = numericalFields[j];

        const chartKey = `scatter-${xField.fieldName}-${yField.fieldName}`;
        if (generatedChartKeys.has(chartKey)) continue;

        prompts.push(
          `请根据数据集创建一个散点图，分析"${xField.fieldName}"和"${yField.fieldName}"两个数值字段之间的相关性。使用散点图类型，X轴为"${xField.fieldName}"，Y轴为"${yField.fieldName}"。请通过散点分布判断两个字段是否存在相关性，并在图表中体现。`
        );
        generatedChartKeys.add(chartKey);
      }
    }

    // 4. 分类字段与数值字段的组合分析
    for (const catField of categoricalFields) {
      for (const numField of numericalFields) {
        if (prompts.length >= maxCharts) break;

        const chartKey = `bar-${catField.fieldName}-${numField.fieldName}`;
        if (generatedChartKeys.has(chartKey)) continue;

        prompts.push(
          `请根据数据集创建一个柱状图，展示不同"${catField.fieldName}"分类下"${numField.fieldName}"字段的平均值。使用柱状图类型，X轴为"${catField.fieldName}"，Y轴为"${numField.fieldName}"的平均值。请确保柱状图能够清晰对比不同分类下的数值差异。`
        );
        generatedChartKeys.add(chartKey);
      }

      if (prompts.length >= maxCharts) break;
    }

    // 5. 时间序列分析
    for (const dateField of dateFields) {
      for (const numField of numericalFields) {
        if (prompts.length >= maxCharts) break;

        const chartKey = `line-${dateField.fieldName}-${numField.fieldName}`;
        if (generatedChartKeys.has(chartKey)) continue;

        prompts.push(
          `请根据数据集创建一个时间序列折线图，展示"${numField.fieldName}"字段随"${dateField.fieldName}"时间的变化趋势。使用折线图类型，X轴为时间"${dateField.fieldName}"，Y轴为"${numField.fieldName}"的数值。请确保折线图能清晰反映数值随时间的变化趋势。`
        );
        generatedChartKeys.add(chartKey);
      }

      if (prompts.length >= maxCharts) break;
    }

    // 如果提示词不够，添加一个通用的数据概览图表
    if (prompts.length < Math.min(maxCharts, 2)) {
      prompts.push(
        `请根据提供的数据集生成一个最能反映数据特征和分布的图表，选择最合适的图表类型来展示数据的核心信息和统计特征。请确保图表具有良好的可读性和数据表达力。`
      );
    }

    return prompts.slice(0, maxCharts);
  }

  /**
   * 构建字段信息上下文
   */
  private buildFieldContext(
    categoricalFields: any[],
    numericalFields: any[],
    dateFields: any[]
  ): string {
    const context = [];

    if (categoricalFields.length > 0) {
      context.push(
        `分类字段：${categoricalFields.map(f => f.fieldName).join(', ')}`
      );
    }

    if (numericalFields.length > 0) {
      context.push(
        `数值字段：${numericalFields.map(f => f.fieldName).join(', ')}`
      );
    }

    if (dateFields.length > 0) {
      context.push(`日期字段：${dateFields.map(f => f.fieldName).join(', ')}`);
    }

    return context.join('；');
  }

  /**
   * 从提示中提取图表标题 - 优化版本
   */
  private extractTitleFromPrompt(
    prompt: string,
    customPrompt?: string
  ): string {
    // 如果有自定义提示词，尝试从中提取更相关的标题
    if (customPrompt && customPrompt.trim()) {
      // 尝试提取关键词作为标题
      const keywords = this.extractKeywordsForTitle(customPrompt);
      if (keywords) {
        return keywords;
      }
    }

    // 从提示中提取字段名和图表类型来生成详细标题
    try {
      // 提取字段名
      const fieldMatches = prompt.match(/"([^"]+)"/g);
      if (fieldMatches && fieldMatches.length > 0) {
        // 清理字段名（去掉引号）
        const fieldNames = fieldMatches.map(name => name.replace(/"/g, ''));

        // 根据提示内容生成详细标题
        if (prompt.includes('柱状图') && prompt.includes('分布')) {
          return `${fieldNames[0]}分类分布统计图`;
        } else if (prompt.includes('饼图') && prompt.includes('占比')) {
          return `${fieldNames[0]}分类占比分析图`;
        } else if (prompt.includes('散点图') && prompt.includes('相关性')) {
          return `${fieldNames[0]}与${fieldNames[1]}相关性分析图`;
        } else if (prompt.includes('时间序列') && prompt.includes('折线图')) {
          return `${fieldNames[1]}随${fieldNames[0]}变化趋势图`;
        } else if (prompt.includes('折线图') && prompt.includes('趋势')) {
          return `${fieldNames[1]}在${fieldNames[0]}中的趋势分析图`;
        } else if (prompt.includes('柱状图') && prompt.includes('平均值')) {
          return `${fieldNames[0]}下${fieldNames[1]}平均值对比图`;
        } else if (prompt.includes('数据特征')) {
          return '数据概览分析图';
        } else if (prompt.includes('主要的图表')) {
          return '核心数据分析图';
        } else if (prompt.includes('分类对比')) {
          return '分类对比分析图';
        } else if (prompt.includes('数值分析')) {
          return '数值统计分析图';
        } else if (prompt.includes('补充图表')) {
          return '补充数据洞察图';
        }
      }
    } catch (error) {
      console.warn('解析图表标题时出错:', error);
    }

    // 默认标题
    if (prompt.includes('柱状图') && prompt.includes('分布')) {
      return '数据分布统计图';
    } else if (prompt.includes('饼图') && prompt.includes('占比')) {
      return '数据占比分析图';
    } else if (prompt.includes('折线图') && prompt.includes('趋势')) {
      return '数据趋势分析图';
    } else if (prompt.includes('散点图') && prompt.includes('相关性')) {
      return '数据相关性分析图';
    } else if (prompt.includes('时间序列')) {
      return '时间序列分析图';
    } else if (prompt.includes('数据特征')) {
      return '数据概览分析图';
    } else if (prompt.includes('主要的图表')) {
      return '核心数据分析图';
    } else if (prompt.includes('分类对比')) {
      return '分类对比分析图';
    } else if (prompt.includes('数值分析')) {
      return '数值统计分析图';
    } else if (prompt.includes('补充图表')) {
      return '补充数据洞察图';
    }

    return 'AI智能分析图表';
  }

  /**
   * 从自定义提示词中提取关键词作为标题 - 增强版
   */
  private extractKeywordsForTitle(customPrompt: string): string {
    const prompt = customPrompt.toLowerCase();

    // 定义一些常见的业务场景关键词
    const keywordMappings = [
      {keywords: ['销售', '营收', '收入'], title: '销售数据深度分析图'},
      {keywords: ['用户', '客户'], title: '用户行为分析图'},
      {keywords: ['产品', '商品'], title: '产品数据分析图'},
      {keywords: ['流量', '访问'], title: '流量趋势分析图'},
      {keywords: ['订单', '交易'], title: '订单数据洞察图'},
      {keywords: ['库存', '仓储'], title: '库存状态分析图'},
      {keywords: ['财务', '财报'], title: '财务数据概览图'},
      {keywords: ['市场', '营销'], title: '市场表现分析图'},
      {keywords: ['趋势', '变化'], title: '数据趋势变化图'},
      {keywords: ['对比', '比较'], title: '多维度对比分析图'},
      {keywords: ['分布', '占比'], title: '数据分布占比图'},
      {keywords: ['质量', '质检'], title: '质量数据分析图'},
      {keywords: ['设备', '机器'], title: '设备运行状态图'},
      {keywords: ['生产', '制造'], title: '生产数据监控图'},
      {keywords: ['停机', '故障'], title: '停机原因分析图'}
    ];

    for (const mapping of keywordMappings) {
      if (mapping.keywords.some(keyword => prompt.includes(keyword))) {
        return mapping.title;
      }
    }

    // 如果没有匹配到特定关键词，返回通用标题
    return '自定义数据洞察图';
  }

  /**
   * 从图表规范中提取图表类型
   */
  private extractChartTypeFromSpec(spec: any): string {
    if (spec.type) {
      return spec.type;
    }
    // 如果没有直接的 type 字段，尝试从其他属性推断
    if (spec.series && spec.series[0]) {
      return spec.series[0].type || 'unknown';
    }
    return 'unknown';
  }

  /**
   * 生成图表布局 - 修复布局尺寸问题
   */
  private generateLayout(index: number, existingChartCount: number = 0) {
    // 计算新图表的实际索引位置
    const actualIndex = existingChartCount + index;

    const layouts = [
      {x: 0, y: 0, w: 6, h: 8},
      {x: 6, y: 0, w: 6, h: 8},
      {x: 0, y: 8, w: 6, h: 8},
      {x: 6, y: 8, w: 6, h: 8},
      {x: 0, y: 16, w: 6, h: 8},
      {x: 6, y: 16, w: 6, h: 8}
    ];

    // 如果超出预定义布局，自动计算位置
    if (actualIndex >= layouts.length) {
      const row = Math.floor(actualIndex / 2);
      const col = actualIndex % 2;
      return {
        i: `chart-${actualIndex}`,
        x: col * 6,
        y: row * 8,
        w: 6,
        h: 8
      };
    }

    return {
      i: `chart-${actualIndex}`,
      ...layouts[actualIndex]
    };
  }
}
